import { l as DocContext, o as Context, k as jsxRuntimeExports, t as textRendererStyle, n as DynamicMath } from "./doenetml-drNYjJiF.js";
import React__default, { useRef, useContext } from "react";
import { j as jxg } from "./JSXGraphRenderer-BcR36ozd.js";
import { BoardContext, TEXT_LAYER_OFFSET } from "./graph-uNmxqd5I.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import { g as getPositionFromAnchorByCoordinate } from "./graph-W6vrFcBu.js";
import { ChoiceInputInlineContext } from "./choiceInput-QYG5RDnq.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { d as detachAnchoredGraphElement, a as attachAnchoredGraphDragHandlers } from "./useAnchoredGraphDragHandler-BEy2dmsI.js";
const number = React__default.memo(function NumberComponent(props) {
  let { componentIdx, id, SVs, actions, callAction } = useDoenetRenderer(props);
  NumberComponent.ignoreActionsWithoutCore = () => true;
  const numberJXG = useRef(null);
  const anchorPointJXG = useRef(null);
  const anchorRel = useRef(null);
  const board = useContext(BoardContext);
  const choiceInputInlineContext = useContext(ChoiceInputInlineContext);
  const pointerState = usePointerDragState();
  const pointAtDown = useRef(null);
  const calculatedX = useRef(null);
  const calculatedY = useRef(null);
  const previousPositionFromAnchor = useRef(null);
  const { fixed, fixLocation, lastPositionFromCore } = useDraggableRefs(SVs, null);
  useBoardPointerTracking(board, pointerState);
  useJSXGraphCleanup({
    objectRef: numberJXG,
    destroy: () => detachAnchoredGraphElement(numberJXG, board)
  });
  const { darkMode } = useContext(DocContext) || {};
  function createNumberJXG() {
    if (board === null) {
      return null;
    }
    let textColor = darkMode === "dark" ? SVs.selectedStyle.textColorDarkMode : SVs.selectedStyle.textColor;
    let backgroundColor = darkMode === "dark" ? SVs.selectedStyle.backgroundColorDarkMode : SVs.selectedStyle.backgroundColor;
    let cssStyle = ``;
    if (backgroundColor) {
      cssStyle += `background-color: ${backgroundColor}`;
    }
    let jsxNumberAttributes = {
      visible: !SVs.hidden,
      fixed: fixed.current,
      layer: 10 * SVs.layer + TEXT_LAYER_OFFSET,
      cssStyle,
      highlightCssStyle: cssStyle,
      strokeColor: textColor,
      strokeOpacity: 1,
      highlightStrokeColor: textColor,
      highlightStrokeOpacity: 0.5,
      highlight: !fixLocation.current,
      parse: false
    };
    let newAnchorPointJXG;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      let anchorCoords = [
        anchor.get_component(0).evaluate_to_constant(),
        anchor.get_component(1).evaluate_to_constant()
      ];
      if (!Number.isFinite(anchorCoords[0])) {
        anchorCoords[0] = 0;
        jsxNumberAttributes["visible"] = false;
      }
      if (!Number.isFinite(anchorCoords[1])) {
        anchorCoords[1] = 0;
        jsxNumberAttributes["visible"] = false;
      }
      newAnchorPointJXG = board.create("point", anchorCoords, {
        visible: false
      });
    } catch (e) {
      jsxNumberAttributes["visible"] = false;
      newAnchorPointJXG = board.create("point", [0, 0], {
        visible: false
      });
    }
    jsxNumberAttributes.anchor = newAnchorPointJXG;
    let { anchorx, anchory } = getPositionFromAnchorByCoordinate(
      SVs.positionFromAnchor
    );
    jsxNumberAttributes.anchorx = anchorx;
    jsxNumberAttributes.anchory = anchory;
    anchorRel.current = [anchorx, anchory];
    let newNumberJXG = board.create(
      "text",
      [0, 0, SVs.text],
      jsxNumberAttributes
    );
    attachAnchoredGraphDragHandlers({
      board,
      newJXG: newNumberJXG,
      newAnchorPoint: newAnchorPointJXG,
      anchorRel,
      pointerState,
      pointAtDown,
      calculatedX,
      calculatedY,
      fixed,
      fixLocation,
      lastPositionFromCore,
      componentIdx,
      actions,
      callAction,
      actionNames: {
        move: "moveNumber",
        focused: "numberFocused",
        clicked: "numberClicked"
      }
    });
    numberJXG.current = newNumberJXG;
    anchorPointJXG.current = newAnchorPointJXG;
    previousPositionFromAnchor.current = SVs.positionFromAnchor;
  }
  if (board) {
    let anchorCoords;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      anchorCoords = [
        anchor.get_component(0).evaluate_to_constant() ?? NaN,
        anchor.get_component(1).evaluate_to_constant() ?? NaN
      ];
    } catch (e) {
      anchorCoords = [NaN, NaN];
    }
    lastPositionFromCore.current = anchorCoords;
    if (numberJXG.current === null) {
      createNumberJXG();
    } else {
      numberJXG.current.relativeCoords.setCoordinates(
        jxg.COORDS_BY_USER,
        [0, 0]
      );
      anchorPointJXG.current?.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        anchorCoords
      );
      numberJXG.current.setText(SVs.text);
      let visible = !SVs.hidden;
      if (Number.isFinite(anchorCoords[0]) && Number.isFinite(anchorCoords[1])) {
        let actuallyChangedVisibility = numberJXG.current.visProp["visible"] !== visible;
        numberJXG.current.visProp["visible"] = visible;
        numberJXG.current.visPropCalc["visible"] = visible;
        if (actuallyChangedVisibility) {
          numberJXG.current.setAttribute({ visible });
        }
      } else {
        numberJXG.current.visProp["visible"] = false;
        numberJXG.current.visPropCalc["visible"] = false;
      }
      let layer = 10 * SVs.layer + TEXT_LAYER_OFFSET;
      let layerChanged = numberJXG.current.visProp.layer !== layer;
      if (layerChanged) {
        numberJXG.current.setAttribute({ layer });
      }
      let textColor = darkMode === "dark" ? SVs.selectedStyle.textColorDarkMode : SVs.selectedStyle.textColor;
      let backgroundColor = darkMode === "dark" ? SVs.selectedStyle.backgroundColorDarkMode : SVs.selectedStyle.backgroundColor;
      let cssStyle = ``;
      if (backgroundColor) {
        cssStyle += `background-color: ${backgroundColor}`;
      } else {
        cssStyle += `background-color: transparent`;
      }
      if (numberJXG.current.visProp.strokecolor !== textColor) {
        numberJXG.current.visProp.strokecolor = textColor;
        numberJXG.current.visProp.highlightstrokecolor = textColor;
      }
      if (numberJXG.current.visProp.cssstyle !== cssStyle) {
        numberJXG.current.visProp.cssstyle = cssStyle;
        numberJXG.current.visProp.highlightcssstyle = cssStyle;
      }
      numberJXG.current.visProp.highlight = !fixLocation.current;
      numberJXG.current.visProp.fixed = fixed.current;
      numberJXG.current.isDraggable = !fixLocation.current;
      numberJXG.current.needsUpdate = true;
      if (SVs.positionFromAnchor !== previousPositionFromAnchor.current) {
        let { anchorx, anchory } = getPositionFromAnchorByCoordinate(
          SVs.positionFromAnchor
        );
        numberJXG.current.visProp.anchorx = anchorx;
        numberJXG.current.visProp.anchory = anchory;
        anchorRel.current = [anchorx, anchory];
        previousPositionFromAnchor.current = SVs.positionFromAnchor;
        numberJXG.current.fullUpdate();
      } else {
        numberJXG.current.update();
      }
      if (anchorPointJXG.current) {
        anchorPointJXG.current.needsUpdate = true;
        anchorPointJXG.current.update();
      }
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id });
  }
  if (SVs.hidden) {
    return null;
  }
  let number2 = SVs.text;
  if (SVs.renderAsMath) {
    number2 = "\\(" + number2 + "\\)";
  }
  const style = !choiceInputInlineContext.inOption ? textRendererStyle(darkMode ?? "light", SVs.selectedStyle) : void 0;
  return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style, id, children: /* @__PURE__ */ jsxRuntimeExports.jsx(DynamicMath, { latex: number2 }) });
});
export {
  number as default
};
